import { Box, Divider, IconButton, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import React, { MouseEvent, useEffect, useReducer, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useCalendarNavigation } from '../../../../customHook/useCalendarNavigation';
import { modalReducer } from '../../../../reducers/modalReducer';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { fetchAllAccounts, handleCalendarConnect } from '../../../../store/slices/calendarSlice';
import zeplinColor from '../../../../theme/Color';
import { CALENDAR_PROVIDERS } from '../../../../utilities/constants';
import { getCalendarOptions } from '../../../../utilities/EventFormContainer.utils';
import CustomAlert from '../../../shared/CustomAlert/CustomAlert';
import CustomAlertContain from '../../../shared/CustomAlert/CustomAlertContain';
import CustomRangePicker from '../../../shared/CustomRangePicker/CustomRangePicker';
import { AccountRow } from './components/AccountRow';
import CalendarAdd from './components/CalendarAdd/CalendarAdd';
import CalendarDisconnect from './components/CalendarDisconnect/CalendarDisconnect';
import CalendarRename from './components/CalendarRename/CalendarRename';
import { CalendarSettingsMenu } from './components/CalendarSettingsMenu';
import { ConnectOnboarding } from './components/ConnectOnboarding';
import { OtherCalendarRow } from './components/OtherAccountRow';

interface CalendarPanelProps {
  onDateChange: (date: Dayjs | null) => void;
  currentMonth: number;
  currentYear: number;
  onMonthYearChange?: (month: number, year: number) => void;
  currentWeekDate?: Dayjs;
  collapsed?: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

const CalendarPanel: React.FC<CalendarPanelProps> = ({
  onDateChange,
  currentMonth,
  currentYear,
  onMonthYearChange,
  currentWeekDate,
  collapsed = false,
  setCollapsed,
}) => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const {
    connections: { isGoogleConnected, isMsConnected, isZoomConnected },
    otherCalendars,
    accounts,
  } = useAppSelector((state) => state.calendar);
  const { handlePreviousMonth, handleNextMonth, handleDateChange } = useCalendarNavigation({
    currentMonth,
    currentYear,
    onMonthYearChange,
    onDateChange,
  });
  const allAccountOptions = getCalendarOptions(accounts);

  const [success, setSuccess] = useState(false);
  const [alertType, setAlertType] = useState('');
  const [calendarProvider, setCalendarProvider] = useState(
    sessionStorage.getItem('calendar-provider') || '',
  );
  const [calendarDetails, setCalendarDetails] = useState<any>();
  const [optionAnchorEl, setOptionAnchorEl] = useState<HTMLImageElement | null>(null);
  const [accordionOpen, setAccordionOpen] = useState(true);
  const [otherCalendarAccordion, setOtherCalendarAccordion] = useState(true);

  const [modalState, modalDispatch] = useReducer(modalReducer, {
    disconnect: false,
    rename: false,
    add: false,
    connect: isZoomConnected || isGoogleConnected || isMsConnected ? false : true,
  });

  const currentDate = currentWeekDate || dayjs().year(currentYear).month(currentMonth);
  const [internalDisplayDate, setInternalDisplayDate] = useState<Dayjs>(
    currentWeekDate || dayjs().year(currentYear).month(currentMonth),
  );

  useEffect(() => {
    const code = location?.search?.split('code=')[1];
    const provider = sessionStorage.getItem('calendar-provider');

    if (code && provider) {
      dispatch(handleCalendarConnect({ code, provider }));
      sessionStorage.removeItem('calendar-provider');
    }
  }, [location.search, dispatch]);

  const handleClick = (event: MouseEvent<HTMLImageElement>) => {
    event.stopPropagation();
    setSuccess(false);
    setOptionAnchorEl(event.currentTarget);
  };

  useEffect(() => {
    setTimeout(() => dispatch(fetchAllAccounts()), 2000);
  }, [dispatch]);

  if (collapsed) {
    return (
      <Box
        sx={{
          padding: '5px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          borderRadius: '50%',
          '&:hover': { backgroundColor: zeplinColor.Neutral4 },
        }}
        onClick={() => setCollapsed((prev) => !prev)}
      >
        <img src={'/assets/collapse-icon.svg'} alt='collapse icon' />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { xs: 'calc(100vh - 300px)', xl: 'calc(100vh - 360px)' },
        border: `1px solid ${zeplinColor.Neutral5}`,
        borderTopLeftRadius: '14px',
        borderTopRightRadius: '14px',
        overflowY: 'scroll',
      }}
    >
      <Box
        sx={{
          padding: { xs: '16px', xl: '24px' },
        }}
      >
        <CustomRangePicker
          onDateChange={handleDateChange}
          currentDateSelected={currentDate}
          onPreviousMonth={handlePreviousMonth}
          onNextMonth={handleNextMonth}
          setCollapsed={setCollapsed}
          internalDisplayDate={internalDisplayDate}
          setInternalDisplayDate={setInternalDisplayDate}
        />
      </Box>

      <Divider sx={{ borderColor: zeplinColor.Neutral5 }} />

      <Box
        sx={{
          padding: { xs: '16px', xl: '24px' },
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant='responsiveHeading4'>My Calendars</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img
              src={'/assets/add.svg'}
              style={{ width: '24px', height: '24px', cursor: 'pointer' }}
              alt='add-calendar'
              onClick={() => modalDispatch({ type: 'OPEN', payload: 'add' })}
            />
            <IconButton
              size='medium'
              onClick={() => setAccordionOpen((prev) => !prev)}
              sx={{ p: 0, ml: 0.5 }}
              aria-label='expand'
            >
              <img
                src={'/assets/down-arrow.svg'}
                style={{
                  transform: accordionOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  width: '24px',
                  height: '24px',
                  color: zeplinColor.BasicBlack,
                }}
                alt='down arrow'
              />
            </IconButton>
          </Box>
        </Box>

        {accordionOpen && (
          <Box sx={{ mt: 1 }}>
            {CALENDAR_PROVIDERS.map(({ type, label }) => {
              const providerAccounts = allAccountOptions.filter((option) => option.type === type);

              return (
                <AccountRow
                  key={label}
                  name={label}
                  accounts={providerAccounts}
                  onSettingsClick={(e, accountValue) => {
                    handleClick(e);
                    setCalendarDetails(accountValue);
                  }}
                />
              );
            })}
          </Box>
        )}

        {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant='responsiveHeading4'>Other Calendars</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <img
              src={'/assets/add.svg'}
              style={{ width: '24px', height: '24px', cursor: 'pointer' }}
              alt='add-calendar'
              onClick={() => modalDispatch({ type: 'OPEN', payload: 'add' })}
            />
            <IconButton
              size='medium'
              onClick={() => setOtherCalendarAccordion((prev) => !prev)}
              sx={{ p: 0, ml: 0.5 }}
              aria-label='expand'
            >
              <img
                src={'/assets/down-arrow.svg'}
                style={{
                  transform: otherCalendarAccordion ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  width: '24px',
                  height: '24px',
                  color: zeplinColor.BasicBlack,
                }}
                alt='down arrow'
              />
            </IconButton>
          </Box>
        </Box>

        {otherCalendarAccordion && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {otherCalendars.map(({ name }: { name: string }) => (
              <OtherCalendarRow
                key={name}
                name={name}
                onSettingsClick={(e) => {
                  handleClick(e);
                  setCalendarProvider(name);
                }}
              />
            ))}
          </Box>
        )} */}
      </Box>
      <Divider sx={{ borderColor: zeplinColor.Neutral5 }} />

      <CalendarSettingsMenu
        anchorEl={optionAnchorEl}
        calendarDetails={calendarDetails}
        onClose={() => setOptionAnchorEl(null)}
        modalDispatch={modalDispatch}
        key={calendarDetails?.value}
      />

      {modalState.connect && (
        <ConnectOnboarding onClose={() => modalDispatch({ type: 'CLOSE', payload: 'connect' })} />
      )}

      <CustomAlert
        open={success && calendarProvider ? true : false}
        handleClose={() => {
          setSuccess(false);
          setAlertType('');
        }}
        message={
          <CustomAlertContain
            alertIcon={
              <img src='/assets/checkmark-green.png' width={24} height={24} alt='checkmark-green' />
            }
            alert={success ? `${alertType} successfully` : ''}
          />
        }
        error={false}
      />

      <CalendarDisconnect
        handleClose={() => modalDispatch({ type: 'CLOSE', payload: 'disconnect' })}
        isModalOpen={modalState.disconnect}
        calendarProvider={calendarProvider}
        calendarDetails={calendarDetails}
      />
      <CalendarRename
        handleClose={() => modalDispatch({ type: 'CLOSE', payload: 'rename' })}
        isModalOpen={modalState.rename}
        calendarDetails={calendarDetails}
        key={modalState.rename.toString() || 'false'}
      />
      <CalendarAdd
        handleClose={() => modalDispatch({ type: 'CLOSE', payload: 'add' })}
        isModalOpen={modalState.add}
      />
    </Box>
  );
};

export default CalendarPanel;
